import { FormControl } from '@chakra-ui/react';
import { SelectPicker } from 'rsuite';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchWithAuth } from '../../utils/apiHelpers';
import { useSelector, useDispatch } from 'react-redux';
import { SelectMenu } from '@spotoninc/nexus-react';
import { useFeatureFlagFromContext } from '../../launchdarkly';
import '../../styles/reskinned-location-dropdown.css';
import '../../styles/location-dropdown-fix.css';
import {
  setCurrency,
  setIntegration,
  setLocationData,
} from '../../redux/slices/locationSlice';
import {
  setCompanySwitcherActive,
  setPeriodData,
  setPeriodFrom,
  setPeriodSwitcher,
  setPeriodTo,
  setLastFetchedIntegration,
  setDefaultDateValue,
} from '../../redux/slices/dateSlice';
import { addEventProperties } from '../../utils/analytics/heapAnalytics';
import { updateIntegrationConnectionStatus } from '../widgets/settings/integrationUtils';

const LocationDropDown = React.memo(
  ({ locationValue, setLocation, multiple = false, height = '32px' }) => {
    // State hooks
    const [loader, setLoader] = useState(false);

    // Redux hooks
    const dispatch = useDispatch();
    const dataLoading = useSelector(state => state.dataFetch.dataLoading);
    const periodFrom = useSelector(state => state.dateFormat.periodFrom);
    const defaultDateValue = useSelector(
      state => state.dateFormat.defaultDateValue
    );
    const locationData = useSelector(
      state => state.locationSelectFormat.locationData
    );
    const periodSwitcher = useSelector(
      state => state.dateFormat.periodSwitcher
    );
    const periodSelect = useSelector(state => state.dateFormat.periodSelect);
    const integration = useSelector(
      state => state.locationSelectFormat.integration
    );
    const periodDataFetched = useSelector(
      state => state.dateFormat.periodDataFetched
    );
    const lastFetchedIntegration = useSelector(
      state => state.dateFormat.lastFetchedIntegration
    );

    // LaunchDarkly feature flags
    const showReskin = useFeatureFlagFromContext('showReskin', false);

    // Memoize the style object
    const selectStyle = useMemo(
      () => ({
        minWidth: '150px',
        width: '100%',
        maxWidth: '370px',
        height: height,
      }),
      [height]
    );

    // Memoize the placeholder value
    const pickerPlaceholder = useMemo(
      () => (locationData.length > 0 ? locationData[0].label : 'Loading'),
      [locationData]
    );

    // Memoize expensive calculations
    const nexusOptions = useMemo(
      () =>
        locationData.map(item => ({
          label: item.label,
          value: item.value,
        })),
      [locationData]
    );

    // Find location by value
    const findLocation = useCallback(
      location => locationData.find(data => data.value === location),
      [locationData]
    );

    // Helper functions
    const fetchPeriod = useCallback(() => {
      // Skip if we've already fetched data for this integration
      if (periodDataFetched && integration === lastFetchedIntegration) {
        return; // Return immediately without a cleanup function
      }

      const url =
        '/api/fetch_periods/' +
        (integration !== null && integration !== undefined
          ? integration
          : 'null');

      const controller = new AbortController();
      const signal = controller.signal;

      // Update the last fetched integration
      dispatch(setLastFetchedIntegration(integration));

      fetchWithAuth(url, {
        method: 'GET',
        signal,
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then(data => {
          if (data.code === undefined) {
            // Process period data
            if (data.period_cal === 'true') {
              const dataNew = data.period_data.map(item => ({
                label: item.period_label,
                value: item.period_label,
              }));

              dispatch(setPeriodData(dataNew)); // This will also set periodDataFetched to true
              dispatch(setPeriodSwitcher(true)); // Enable period switcher when periods are available
              if (periodFrom === '') {
                // Batch related state updates
                dispatch(setPeriodFrom(data.from_period));
                dispatch(setPeriodTo(data.to_period));
              }
            } else {
              dispatch(setPeriodSwitcher(false));
              // This will also set periodDataFetched to false
            }

            // Set company period calendar status
            dispatch(
              setCompanySwitcherActive(data.company_period_cal === 'true')
            );
          } else {
            window.open('/', '_self');
            alert('Session Expired!.');
          }
        })
        .catch(err => {
          if (err.name !== 'AbortError') {
            console.error('Error fetching period data:', err);
          }
        });

      return () => controller.abort();
    }, [
      integration,
      periodFrom,
      dispatch,
      periodDataFetched,
      lastFetchedIntegration,
    ]);

    // Handle location or integration selection
    const handleLocationSelect = useCallback(
      val => {
        if (val === undefined) return;

        setLoader(true);
        try {
          const selected = findLocation(val);
          if (!selected) return;

          // If the user selected an integration-level option (may have no locations)
          if (selected.isIntegration) {
            // Set the current integration and let fetchPeriod determine period settings
            dispatch(setIntegration(selected.integrationId));
            // Don't reset period-related state here - let fetchPeriod handle it
            // This allows the date range to persist when switching locations in the same mode
            // Fetch period data for this integration (defer to let integration update)
            setTimeout(() => fetchPeriod(), 100);
            return;
          }

          // Otherwise, a specific location was selected
          // Update currency
          dispatch(setCurrency(selected.currency));

          // Process period switcher settings using ddl_value
          const isPeriodSwitcherEnabled =
            selected.ddl_value.split('|')[3].split('=')[1] !== 'false';

          // Only reset date/period values if we're switching between modes
          // If staying in the same mode, keep the current date range
          const isSwitchingModes = periodSwitcher !== isPeriodSwitcherEnabled;

          if (isPeriodSwitcherEnabled) {
            // Batch related state updates
            dispatch(setPeriodSwitcher(true));
            dispatch(setIntegration(selected.ddl_value.split('|')[1]));
            // Only reset date values if we're switching from date mode to period mode
            if (isSwitchingModes) {
              dispatch(setDefaultDateValue(undefined));
            }
            // Defer to allow integration state to update before fetching periods
            setTimeout(() => fetchPeriod(), 100);
          } else {
            // Batch related state updates
            dispatch(setPeriodSwitcher(false));
            // Only reset period values if we're switching from period mode to date mode
            if (isSwitchingModes) {
              dispatch(setPeriodFrom(''));
              dispatch(setPeriodTo(''));
            }
          }
        } finally {
          setLoader(false);
        }
      },
      [findLocation, dispatch, fetchPeriod, periodSwitcher]
    );

    // Fetch locations and integrations data for the overview dropdown
    const fetchLocations = useCallback(() => {
      // Check if locationData has the complex format (with integration-level options)
      // If it does, skip the fetch. If not, fetch to get the proper format.
      const hasComplexFormat =
        locationData.length > 0 &&
        locationData.some(item => item.isIntegration === true);

      if (!hasComplexFormat) {
        const controller = new AbortController();
        const signal = controller.signal;

        // Fetch authorized locations, all integrations, and auth data in parallel
        const locationsReq = fetchWithAuth('/api/fetch_locations/', {
          method: 'POST',
          signal,
        });
        const integrationsReq = fetchWithAuth('/api/auth_update/', {
          method: 'GET',
          signal,
        });
        const authReq = fetchWithAuth('/api/fetch_auth_data/', {
          method: 'GET',
          signal,
        });

        Promise.all([locationsReq, integrationsReq, authReq])
          .then(async ([locRes, intRes, authRes]) => {
            if (!locRes.ok) throw new Error('Failed to fetch locations');
            if (!intRes.ok) throw new Error('Failed to fetch integrations');
            if (!authRes.ok) throw new Error('Failed to fetch auth data');
            const [locData, intData, authData] = await Promise.all([
              locRes.json(),
              intRes.json(),
              authRes.json(),
            ]);

            // Update Heap event properties with integration connection status
            updateIntegrationConnectionStatus(
              intData.integration,
              addEventProperties
            );

            // Try to determine current user's privilege id from auth data
            const currentUserId = Number(
              window?.localStorage?.getItem('currentUserId') || 0
            );
            const currentEmail =
              (typeof window !== 'undefined' &&
              window.localStorage?.getItem('userInfo')
                ? (() => {
                    try {
                      return JSON.parse(window.localStorage.getItem('userInfo'))
                        .email;
                    } catch (_) {
                      return null;
                    }
                  })()
                : null) || null;

            const idEq = (a, b) => String(a) === String(b);
            const emailEq = (a, b) =>
              String(a || '').toLowerCase() === String(b || '').toLowerCase();

            const me = Array.isArray(authData?.users)
              ? authData.users.find(
                  u =>
                    idEq(u.user_id_id, currentUserId) ||
                    idEq(u.user_id, currentUserId) ||
                    idEq(u.id, currentUserId) ||
                    (currentEmail &&
                      (emailEq(u.email, currentEmail) ||
                        emailEq(u.email_id, currentEmail) ||
                        emailEq(u.emailId, currentEmail)))
                )
              : undefined;
            const directPrivId =
              me?.priviledge_id_id ||
              me?.privilege_id_id ||
              me?.priviledge_id ||
              me?.privilege_id ||
              me?.priv_id;

            // Resolve by matching priv_description to authData.priviledges label if needed
            let resolvedPrivId = directPrivId;
            if (
              !resolvedPrivId &&
              me?.priv_description &&
              Array.isArray(authData?.priviledges)
            ) {
              const targetLabel = String(me.priv_description)
                .toLowerCase()
                .trim();
              const match = authData.priviledges.find(
                p =>
                  String(p?.label || '')
                    .toLowerCase()
                    .trim() === targetLabel
              );
              if (match && match.value !== undefined && match.value !== null) {
                resolvedPrivId = match.value;
              }
            }

            let assignedIntegrationIds = new Set();
            const locationCountByIntegration = new Map();
            if (resolvedPrivId) {
              try {
                const privRes = await fetchWithAuth(
                  `/api/get_priv_data/${resolvedPrivId}/`,
                  { method: 'GET', signal }
                );
                if (privRes.ok) {
                  const privJson = await privRes.json();
                  const assigned = privJson?.assigned_integrations || [];
                  assignedIntegrationIds = new Set(
                    (assigned || []).map(id => String(id))
                  );

                  // Count locations per integration to determine which integration-level
                  // entries to show. If an integration has exactly 1 location, hide the
                  // integration-level entry (it would show duplicate data)
                  Object.keys(privJson)
                    .filter(key => !isNaN(key)) // Get numeric keys (integration IDs)
                    .forEach(intId => {
                      if (Array.isArray(privJson[intId])) {
                        const count = privJson[intId].filter(
                          loc => loc.assigned === true
                        ).length;
                        locationCountByIntegration.set(String(intId), count);
                      }
                    });
                }
              } catch (e) {
                // Ignore and fallback to locations-only filtering
              }
            }

            // Normalize location items (as before), also capture integrationId for grouping
            const locationItems = Array.isArray(locData)
              ? locData.map(item => ({
                  label: item.ui_label,
                  value: item.ui_label,
                  currency: item.currency,
                  ddl_value: item.ddl_value,
                  period_length: item.period_length,
                  // Capture location_id_id from API response for filtering
                  locationId: item.location_id_id,
                  // Extract integration id from ddl_value (format: <...>|<integrationId>|...)
                  integrationId:
                    typeof item.ddl_value === 'string'
                      ? item.ddl_value.split('|')[1]
                      : null,
                }))
              : [];

            // Normalize integrations from auth_update
            const allIntegrations = Array.isArray(intData?.integration)
              ? intData.integration
              : [];

            // Build synthetic integration-level options (include even if no locations)
            let integrationItems = allIntegrations.map(intg => ({
              label: intg.app_name || String(intg.id),
              value: `INTEGRATION::${intg.id}`,
              isIntegration: true,
              integrationId: String(intg.id),
              location_attr: !!intg.location_attr,
            }));

            // Filter integration options to the user's assigned integrations if available; otherwise restrict to those present in locations
            if (assignedIntegrationIds.size > 0) {
              integrationItems = integrationItems.filter(item =>
                assignedIntegrationIds.has(item.integrationId)
              );
            } else {
              const allowedFromLocations = new Set(
                locationItems
                  .map(x => x.integrationId)
                  .filter(id => id !== null && id !== undefined)
              );
              integrationItems = integrationItems.filter(item =>
                allowedFromLocations.has(item.integrationId)
              );
            }

            // Filter out integration-level entries for integrations with exactly 1 location
            // since they would show the same data as the single location
            integrationItems = integrationItems.filter(item => {
              const locationCount = locationCountByIntegration.get(
                item.integrationId
              );
              // Show if: no count data (fallback), 0 locations, or 2+ locations
              // Hide if exactly 1 location (would show duplicate data)
              return locationCount === undefined || locationCount !== 1;
            });

            // Helper function to normalize strings for comparison (remove periods, spaces, lowercase)
            const normalizeString = str =>
              str.toLowerCase().replace(/[.\s]/g, '');

            // Create a map of integrationId -> normalized app_name to detect duplicates
            const integrationIdToNormalizedName = new Map();
            allIntegrations.forEach(intg => {
              if (intg.id && intg.app_name) {
                integrationIdToNormalizedName.set(
                  String(intg.id),
                  normalizeString(intg.app_name)
                );
              }
            });

            // Create a set of integrationIds that are already in integrationItems
            const existingIntegrationIds = new Set(
              integrationItems.map(item => item.integrationId)
            );

            // Filter locationItems to remove duplicates of integration-level entries
            // A locationItem is considered a duplicate if:
            // 1. Its integrationId is already in integrationItems
            // 2. Its label (normalized) matches the integration's app_name (normalized)
            const deduplicatedLocationItems = locationItems.filter(locItem => {
              if (
                !locItem.integrationId ||
                !existingIntegrationIds.has(locItem.integrationId)
              ) {
                // Keep if no integrationId or if integration not in integrationItems
                return true;
              }

              // Check if this location's label matches the integration name
              const normalizedLabel = normalizeString(locItem.label);
              const normalizedIntegrationName =
                integrationIdToNormalizedName.get(locItem.integrationId);

              // Keep the location if its label doesn't match the integration name
              // (meaning it's a specific location, not a duplicate integration entry)
              return normalizedLabel !== normalizedIntegrationName;
            });

            // Decide initial state and ordering: integrations first, then locations
            const finalItems = [
              ...integrationItems,
              ...deduplicatedLocationItems,
            ];

            if (locationItems.length > 0) {
              // Populate options; default select whichever is first in finalItems
              dispatch(setLocationData(finalItems));

              const firstOption = finalItems[0];
              const firstIntegrationId = firstOption?.integrationId;
              if (firstIntegrationId) {
                dispatch(setIntegration(firstIntegrationId));
              }

              // Set currency from the first option if available, else fallback to first location's currency
              if (firstOption?.currency) {
                dispatch(setCurrency(firstOption.currency));
              } else if (locData && locData[0]?.currency) {
                dispatch(setCurrency(locData[0].currency));
              }

              // Auto-select the first option if none selected
              if (!locationValue && firstOption) {
                setLocation(firstOption.value);
              }

              // Conditionally fetch period data based on current rules
              const needPeriodData =
                !defaultDateValue &&
                !(periodSwitcher && periodSelect) &&
                (!periodDataFetched ||
                  lastFetchedIntegration !== firstIntegrationId);

              if (needPeriodData) {
                setTimeout(() => fetchPeriod(), 100);
              }
            } else {
              // No locations returned, but we may still have integrations (filtered by privilege)
              dispatch(setLocationData(integrationItems));

              if (integrationItems.length > 0) {
                const firstIntegrationId = integrationItems[0].integrationId;
                dispatch(setIntegration(firstIntegrationId));
                // Auto-select the first integration-level option
                if (!locationValue) {
                  setLocation(integrationItems[0].value);
                }
                // Fetch period data for this integration
                setTimeout(() => fetchPeriod(), 100);
              }
            }
          })
          .catch(err => {
            if (err.name !== 'AbortError') {
              console.error('Error fetching locations/integrations:', err);
            }
          });

        return () => controller.abort();
      }

      // Only call handleLocationSelect if locationValue is defined
      if (locationValue) {
        handleLocationSelect(locationValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      locationData.length,
      locationValue,
      defaultDateValue,
      periodSwitcher,
      periodSelect,
      periodDataFetched,
      lastFetchedIntegration,
    ]);

    // Handle change for Nexus SelectMenu
    const handleNexusChange = useCallback(
      (_, value) => {
        handleLocationSelect(value);
        setLocation(value);
      },
      [handleLocationSelect, setLocation]
    );

    // Handle change for SelectPicker
    const handlePickerSelect = useCallback(
      val => {
        handleLocationSelect(val);
        setLocation(val);
      },
      [handleLocationSelect, setLocation]
    );

    // Effect hook with proper dependency array
    useEffect(() => {
      const cleanup = fetchLocations();
      return cleanup;
    }, [fetchLocations]);

    return (
      <FormControl w="auto">
        {showReskin ? (
          // Nexus SelectMenu when showReskin is true
          <SelectMenu
            multiple={multiple}
            className="reskinned-location-dropdown"
            options={nexusOptions}
            value={locationValue !== undefined ? locationValue : null}
            placeholder="Select location"
            onChange={handleNexusChange}
            style={selectStyle}
          />
        ) : (
          // Original SelectPicker when showReskin is false
          <SelectPicker
            loading={dataLoading || loader}
            data={locationData}
            value={locationValue !== undefined ? locationValue : ''}
            size="sm"
            placeholder={pickerPlaceholder}
            style={selectStyle}
            onSelect={handlePickerSelect}
            className="location-dropdown-fix"
          />
        )}
      </FormControl>
    );
  }
);

export default LocationDropDown;
